import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FaSave, FaTimes } from "react-icons/fa";

import {
  formatDateForInput,
  normalizeAdherentPayload,
  validateAdherentForm,
} from "../utils/inscriptionsUtils";

const INITIAL_FORM = {
  adherent_groupe: "",
  nom: "",
  prenom: "",
  email: "",
  email_secondaire: "",
  tel: "",
  tel_secondaire: "",
  adresse: "",
  cp: "",
  commune: "",
  genre: "M",
  date_naissance: "",
  poids: "",
  licence_number: "",
  autorisation_photo: true,
  notes_adherent: "",
};

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-orange-400 focus:ring-4 focus:ring-orange-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-orange-500/10";

const labelClass =
  "mb-1.5 block text-sm font-bold text-slate-700 dark:text-slate-200";

function TextInput({
  label,
  name,
  value,
  onChange,
  type = "text",
  required = false,
  placeholder = "",
  inputMode,
}) {
  return (
    <div>
      <label className={labelClass}>
        {label}
        {required && <span className="text-rose-500"> *</span>}
      </label>

      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        inputMode={inputMode}
        className={inputClass}
      />
    </div>
  );
}

export default function AdherentForm({
  mode = "create",
  adherent,
  groups = [],
  saving = false,
  onSubmit,
  onClose,
}) {
  const initialForm = useMemo(() => {
    if (!adherent) return INITIAL_FORM;

    return {
      ...INITIAL_FORM,
      ...adherent,
      date_naissance: formatDateForInput(adherent.date_naissance),
      autorisation_photo:
        adherent.autorisation_photo === null ||
        adherent.autorisation_photo === undefined
          ? true
          : Boolean(adherent.autorisation_photo),
    };
  }, [adherent]);

  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    setFormData(initialForm);
  }, [initialForm]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const payload = normalizeAdherentPayload(formData);
    const errorMessage = validateAdherentForm(payload);

    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }

    await onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <section className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
        <h3 className="text-base font-bold text-slate-950 dark:text-white">
          Informations sportives
        </h3>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <label className={labelClass}>
              Groupe <span className="text-rose-500">*</span>
            </label>

            <select
              name="adherent_groupe"
              value={formData.adherent_groupe}
              onChange={handleChange}
              required
              className={inputClass}
            >
              <option value="">Sélectionnez un groupe</option>
              {groups.map((group) => (
                <option key={group.id || group.nom} value={group.nom}>
                  {group.nom}
                </option>
              ))}
            </select>
          </div>

          <TextInput
            label="N° de licence"
            name="licence_number"
            value={formData.licence_number}
            onChange={handleChange}
            placeholder="Ex : M10071971VICTO01"
          />

          <TextInput
            label="Poids"
            name="poids"
            value={formData.poids}
            onChange={handleChange}
            placeholder="Ex : 40,5"
            inputMode="decimal"
          />

          <div>
            <label className={labelClass}>
              Autorisation prise de photo
            </label>

            <label className="flex cursor-pointer items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900">
              <span className="text-sm text-slate-600 dark:text-slate-300">
                Autorisation accordée
              </span>

              <input
                type="checkbox"
                name="autorisation_photo"
                checked={Boolean(formData.autorisation_photo)}
                onChange={handleChange}
                className="h-5 w-5 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
              />
            </label>
          </div>
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
        <h3 className="text-base font-bold text-slate-950 dark:text-white">
          Identité
        </h3>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <TextInput
            label="Nom"
            name="nom"
            value={formData.nom}
            onChange={handleChange}
            required
          />

          <TextInput
            label="Prénom"
            name="prenom"
            value={formData.prenom}
            onChange={handleChange}
            required
          />

          <div>
            <label className={labelClass}>
              Genre <span className="text-rose-500">*</span>
            </label>

            <div className="grid grid-cols-2 gap-2">
              <label
                className={`cursor-pointer rounded-2xl border px-4 py-3 text-center text-sm font-bold transition ${
                  formData.genre === "M"
                    ? "border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-500/40 dark:bg-blue-500/10 dark:text-blue-300"
                    : "border-slate-200 bg-white text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                }`}
              >
                <input
                  type="radio"
                  name="genre"
                  value="M"
                  checked={formData.genre === "M"}
                  onChange={handleChange}
                  className="sr-only"
                />
                Masculin
              </label>

              <label
                className={`cursor-pointer rounded-2xl border px-4 py-3 text-center text-sm font-bold transition ${
                  formData.genre === "F"
                    ? "border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-300"
                    : "border-slate-200 bg-white text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                }`}
              >
                <input
                  type="radio"
                  name="genre"
                  value="F"
                  checked={formData.genre === "F"}
                  onChange={handleChange}
                  className="sr-only"
                />
                Féminin
              </label>
            </div>
          </div>

          <TextInput
            label="Date de naissance"
            name="date_naissance"
            type="date"
            value={formData.date_naissance}
            onChange={handleChange}
            required
          />
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
        <h3 className="text-base font-bold text-slate-950 dark:text-white">
          Contact
        </h3>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <TextInput
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
          />

          <TextInput
            label="Email secondaire"
            name="email_secondaire"
            type="email"
            value={formData.email_secondaire}
            onChange={handleChange}
          />

          <TextInput
            label="Téléphone"
            name="tel"
            value={formData.tel}
            onChange={handleChange}
            required
            placeholder="06 00 00 00 00"
            inputMode="tel"
          />

          <TextInput
            label="Téléphone secondaire"
            name="tel_secondaire"
            value={formData.tel_secondaire}
            onChange={handleChange}
            placeholder="06 00 00 00 00"
            inputMode="tel"
          />
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
        <h3 className="text-base font-bold text-slate-950 dark:text-white">
          Adresse
        </h3>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <TextInput
              label="Adresse"
              name="adresse"
              value={formData.adresse}
              onChange={handleChange}
              required
            />
          </div>

          <TextInput
            label="Code postal"
            name="cp"
            value={formData.cp}
            onChange={handleChange}
            required
            inputMode="numeric"
          />

          <TextInput
            label="Commune"
            name="commune"
            value={formData.commune}
            onChange={handleChange}
            required
          />
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
        <label className={labelClass}>Notes</label>

        <textarea
          name="notes_adherent"
          value={formData.notes_adherent}
          onChange={handleChange}
          rows={5}
          placeholder="Informations utiles pour les enseignants ou le bureau..."
          className={inputClass}
        />
      </section>

      <div className="sticky bottom-0 -mx-5 border-t border-slate-200 bg-white/90 px-5 py-4 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-slate-950/20 transition hover:-translate-y-0.5 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
          >
            <FaSave />
            {saving
              ? "Enregistrement..."
              : mode === "create"
              ? "Créer l'adhérent"
              : "Enregistrer"}
          </button>
        </div>
      </div>
    </form>
  );
}